import React from 'react';
import ReactDOM from 'react-dom/client';
import './src/i18n/config';
import App from './App';
import { ClerkProvider } from '@clerk/clerk-react';
import './src/app/globals.css';

import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './src/lib/queryClient';
import { RouteScrollToTop } from './src/components/RouteScrollToTop';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { useStore } from './src/lib/store';
import { useTranslation } from 'react-i18next';
import { enUS, esES, frFR, deDE, ptBR, jaJP, zhCN, ruRU, arSA, hiIN } from '@clerk/localizations';
import { initSentry, wrapApp } from './services/sentryService';
import { captureFirstTouch } from './services/acquisitionAnalytics';

captureFirstTouch();

// SILENT PURGE PROTOCOL
const isDev = import.meta.env.DEV;

// Vercel Analytics and Speed Insights are cookieless (no cookies, no stored
// IP, no cross-day visitor ID), so they run for every visitor (src/lib/consent.ts).
const CookielessAnalytics = () => (
  <>
    <Analytics />
    <SpeedInsights />
  </>
);
const PURGE_KEY = 'cartlens_purge_1.5.0';

// HARD DISABLE IN DEV: Block registration and purge existing workers
if ('serviceWorker' in navigator) {
  if (isDev) {
    console.warn('[PWA] Development mode: Service Workers are HARD DISABLED to prevent resource leaks.');
    // Override register to do nothing
    (navigator.serviceWorker as any).register = () => Promise.resolve({});
    // Unregister any active ones
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (let r of registrations) {
        console.log('[PWA] Dev Purge:', r.active?.scriptURL);
        r.unregister();
      }
    });
  } else if (!localStorage.getItem(PURGE_KEY)) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (let r of registrations) {
        console.log('[PWA] Migration Purge:', r.active?.scriptURL);
        r.unregister();
      }
      localStorage.setItem(PURGE_KEY, 'true');
    });
  }
}

// Initialize Sentry Proactive Observability
initSentry();

const SentryApp = wrapApp(App);

// CONSOLE LOG SUPPRESSION:
// Hide logs on Production and Preview environments to keep the console clean.
const currentHostname = window.location.hostname;
const isProductionOrPreview = 
  import.meta.env.PROD || 
  import.meta.env.VITE_VERCEL_ENV === 'production' || 
  import.meta.env.VITE_VERCEL_ENV === 'preview' ||
  currentHostname === 'cartlens.co' || 
  currentHostname === 'www.cartlens.co' || 
  currentHostname.endsWith('.vercel.app');

if (isProductionOrPreview) {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.warn = () => {};
  // Keeping console.error for critical runtime failures only
}

// Global Leaflet Icon Fix - Ensures default markers display in all environments (Vite/Production)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// RUNTIME IDENTITY RESOLUTION:
// Dynamically resolve Clerk keys and domain status to prevent initialization crashes.
const resolveIdentityContext = () => {
  const hostname = window.location.hostname; 
  const isLiveDomain = hostname === 'cartlens.co' || hostname === 'www.cartlens.co' || hostname.endsWith('.vercel.app');
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  // localhost SAFETY: Clerk production keys strictly forbid localhost.
  // We use a dummy key to let the app boot for PWA testing.
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && publishableKey?.startsWith('pk_live_')) {
    console.warn("[Identity] Production key detected on localhost. Using placeholder to allow boot for PWA testing.");
    return {
      CLERK_PUBLISHABLE_KEY: "pk_test_Y2FydGxlbnMtZHVtbXkta2V5LTEyMy5jbGVyay5hY2NvdW50cy5kZXYk",
      IS_LIVE_DOMAIN: false
    };
  }

  // HARD GATE: Prevent production key misuse on Vercel preview subdomains.
  if (!isLiveDomain && hostname !== 'localhost' && publishableKey?.startsWith('pk_live_')) {
    return { 
      CLERK_PUBLISHABLE_KEY: "BLOCK_PRODUCTION_KEY_ON_DEV_DOMAIN",
      IS_LIVE_DOMAIN: false
    };
  }

  return {
    CLERK_PUBLISHABLE_KEY: publishableKey,
    IS_LIVE_DOMAIN: isLiveDomain
  };
};

const { CLERK_PUBLISHABLE_KEY } = resolveIdentityContext();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Custom Swahili localization for Clerk (not available in @clerk/localizations)
const swTZ = {
  ...enUS,
  signIn: {
    start: {
      title: "Ingia kwenye CartLens",
      subtitle: "ili kuendelea na Spending DNA",
      actionLink: "Jisajili",
      actionText: "Huna akaunti?"
    },
    password: {
      title: "Ingiza nywila yako",
      subtitle: "Ingiza nywila inayohusiana na akaunti yako",
      actionLink: "Umesahau nywila?"
    }
  },
  signUp: {
    start: {
      title: "Unda akaunti",
      subtitle: "ili kuanza kuokoa na CartLens",
      actionLink: "Ingia",
      actionText: "Tayari una akaunti?"
    },
    emailCode: {
      title: "Thibitisha barua pepe yako",
      subtitle: "Nambari ya uthibitisho imetumwa kwa {{emailAddress}}",
      formTitle: "Nambari ya uthibitisho",
      formSubtitle: "Ingiza nambari iliyotumwa kwako",
      resendButton: "Tuma tena nambari"
    }
  },
  socialButtonsBlockButton: "Endelea na {{provider|titleize}}",
  dividerText: "au",
  lastAuthenticationStrategy: "Ilitumika mwisho",
  formFieldLabel__firstName: "Jina la kwanza",
  formFieldLabel__lastName: "Jina la ukoo",
  formFieldLabel__emailAddress: "Barua pepe",
  formFieldLabel__password: "Nywila",
  formFieldInputPlaceholder__firstName: "Jina la kwanza",
  formFieldInputPlaceholder__lastName: "Jina la ukoo",
  formFieldInputPlaceholder__emailAddress: "Ingiza barua pepe",
  formFieldInputPlaceholder__password: "Ingiza nywila",
  formFieldHintText__optional: "Hiari",
  formFieldAction__forgotPassword: "Umesahau nywila?",
  formButtonPrimary: "Endelea",
  footerPageLink__help: "Msaada",
  footerPageLink__privacy: "Faragha",
  footerPageLink__terms: "Vigezo"
};

// Hoisted to module scope so these are stable references across renders —
// passing new object literals to ClerkProvider on every ClerkWrapper render
// (e.g. on unrelated store updates) makes Clerk tear down and reinitialize
// its internal instance, which races with in-flight sign-in/sign-up steps.
const CLERK_EXPERIMENTAL = { billing: true };
const CLERK_APPEARANCE = {
  variables: {
    fontFamily: '"Outfit", sans-serif',
    colorPrimary: '#10B981',
    colorBackground: '#0A0F1E',
    colorText: '#ffffff',
    colorTextSecondary: 'rgba(255,255,255,0.6)',
    colorInputBackground: 'rgba(255,255,255,0.05)',
    colorInputText: '#ffffff',
    borderRadius: '1rem',
  },
  elements: {
    card: 'bg-[#0A0F1E] border border-white/10 shadow-2xl mx-auto',
    navbar: 'bg-[#0D1526] border-r border-white/5',
    navbarButton: 'text-white/70 hover:text-white transition-all uppercase text-[10px] font-black tracking-widest !text-white',
    navbarButton__active: 'text-white bg-white/10 shadow-sm !text-white',
    navbarButtonIcon: 'text-[#10B981] !text-[#10B981]',
    navbarMobileMenuButton: 'text-white !text-white',
    navbarMobileMenuRow: 'bg-[#0D1526] border-b border-white/5 !text-white',
    headerTitle: 'font-outfit font-black uppercase tracking-tighter text-white !text-white',
    headerSubtitle: 'text-white/40 font-bold !text-white/40',
    profileSectionTitleText: 'text-white font-black uppercase tracking-tighter border-b border-white/5 pb-2 !text-white',
    profileSectionPrimaryButton: 'text-[#10B981] hover:text-[#34D399] font-bold !text-[#10B981]',
    formFieldAction: 'text-[#10B981] hover:text-[#34D399] font-bold !text-[#10B981]',
    breadcrumbsItem: 'text-white/40 font-bold !text-white/40',
    breadcrumbsSeparator: 'text-white/20 !text-white/20',
    pageScrollBox: 'bg-[#0A0F1E]',
    profilePage: 'bg-[#0A0F1E]',
    userButtonPopoverCard: 'bg-[#0A0F1E] border border-white/10 shadow-2xl',
    userButtonPopoverActionButton: 'hover:bg-white/5 transition-all !text-white',
    userButtonPopoverActionButtonText: 'font-bold text-white !text-white',
    userButtonPopoverActionButtonIcon: 'text-[#10B981] !text-[#10B981]',
    userButtonPopoverFooter: 'hidden',
    formButtonPrimary: 'bg-[#10B981] text-[#0A0F1E] font-black uppercase tracking-widest hover:bg-[#34D399] h-[52px] !h-[52px] !bg-[#10B981] !text-[#0A0F1E]',
    formFieldLabel: 'text-white/60 font-black uppercase text-[10px] tracking-widest !text-white/60',
    formFieldInput: 'bg-white/5 border-white/10 text-white focus:border-[#10B981]/50 h-[52px] !h-[52px] text-base !text-base px-4 !px-4 transition-all !text-white',
    footerActionLink: 'text-[#10B981] hover:text-[#34D399] font-bold !text-[#10B981]',
    footer: 'hidden',
    identityPreviewTextPrimary: 'text-white font-bold !text-white',
    identityPreviewTextSecondary: 'text-white/40 font-medium !text-white/40',
    formResendCodeLink: 'text-[#10B981] font-bold hover:text-[#34D399] !text-[#10B981]',
    userButtonTrigger: 'focus:shadow-none focus:ring-0',
    userPreviewMainIdentifier: 'text-white font-bold !text-white',
    userPreviewSecondaryIdentifier: 'text-white/40 !text-white/40',
    badge: 'bg-white/10 border-white/5 text-white !bg-white/10 !border-white/5 !text-white',
    badgeText: 'text-white font-bold !text-white',
    profileSectionContent: 'text-white !text-white',
    textButton: 'text-[#10B981] hover:text-[#34D399] font-bold !text-[#10B981]',
    formHeaderTitle: 'text-white !text-white',
    formHeaderSubtitle: 'text-white/50 !text-white/50',
    tabButton: 'text-white/60 hover:text-white transition-all !text-white/60',
    tabButtonText: 'font-bold uppercase tracking-widest text-[10px] !text-white/60',
    menuButton: 'text-white/60 hover:text-white transition-all !text-white/60',
    menuButtonText: 'font-bold uppercase tracking-widest text-[10px] !text-white/60',
    dividerRow: 'bg-transparent !bg-transparent',
    dividerText: 'text-white/20 !text-white/20 bg-transparent !bg-transparent',
    profileSection: 'border-b border-white/5 !border-white/5',
    accordionTriggerButton: 'text-white hover:bg-white/5 !text-white',
    accordionContent: 'text-white/60 !text-white/60',
    alert: 'bg-rose-500/10 border border-rose-500/20 text-rose-500 !bg-rose-500/10 !border-rose-500/20 !text-rose-500',
    alertText: 'text-rose-500 !text-rose-500',
    formFieldHintText: 'text-white/40 !text-white/40',
    formFieldInfoText: 'text-white/40 !text-white/40',
    formFieldSuccessText: 'text-[#10B981] !text-[#10B981]',
    formFieldErrorText: 'text-rose-500 !text-rose-500',
    alternativeMethodsBlockButton: 'border-white/10 text-white hover:bg-white/5 h-[52px] !h-[52px] !border-white/10 !text-white',
    alternativeMethodsBlockButtonArrow: 'text-white/40 !text-white/40',
    socialButtonsBlockButton: 'border-white/10 text-white hover:bg-white/5 h-[52px] !h-[52px] !border-white/10 !text-white',
    providerIcon: 'grayscale brightness-200',
  }
};

const ClerkWrapper = () => {
  const language = useStore(state => state.language);
  const { t } = useTranslation();
  
  const localization = React.useMemo(() => {
    let base;
    switch (language) {
      case 'es': base = esES; break;
      case 'fr': base = frFR; break;
      case 'de': base = deDE; break;
      case 'pt': base = ptBR; break;
      case 'ja': base = jaJP; break;
      case 'zh': base = zhCN; break;
      case 'ru': base = ruRU; break;
      case 'ar': base = arSA; break;
      case 'hi': base = hiIN; break;
      case 'sw': base = swTZ; break;
      default: base = enUS;
    }

    // Deep merge or specific override for the legal text in Clerk's PaymentElement
    return {
      ...base,
      unstable_billing: {
        ...((base as any).unstable_billing || {}),
        paymentElement: {
          ...((base as any).unstable_billing?.paymentElement || {}),
          terms: t('account.legal_disclosure')
        }
      }
    };
  }, [language, t]);

  return (
    <ClerkProvider 
      publishableKey={CLERK_PUBLISHABLE_KEY}
      localization={localization}
      telemetry={false}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignInUrl="/home"
      afterSignUpUrl="/home"
      experimental={CLERK_EXPERIMENTAL}
      appearance={CLERK_APPEARANCE}
    >
        <SentryApp />
        <CookielessAnalytics />
    </ClerkProvider>
  );
};

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteScrollToTop />
        <ClerkWrapper />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
